import React from "react";
import { FaExclamationTriangle, FaHome, FaRedo } from "react-icons/fa";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Log to error reporting service (if available)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService(error, errorInfo) {
    // You can integrate with error reporting services here:
    // - Sentry
    // - LogRocket
    // - Rollbar
    // - Your own error logging endpoint
    
    try {
      // Example: Send to your backend
      // fetch("/api/errors", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({
      //     error: error.toString(),
      //     errorInfo: errorInfo.componentStack,
      //     url: window.location.href,
      //     userAgent: navigator.userAgent,
      //     timestamp: new Date().toISOString(),
      //   }),
      // }).catch(() => {}); // Silently fail if logging service is down
    } catch (loggingError) {
      console.error("Failed to log error:", loggingError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    
    // Optionally reload the page
    if (this.props.resetOnError) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI based on props
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full text-center bg-white rounded-xl shadow-lg p-8">
            {/* Error Icon */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
                <FaExclamationTriangle className="text-4xl text-red-500" />
              </div>
              <div className="w-24 h-1 bg-gradient-to-r from-[#f26522] to-[#f5a623] mx-auto rounded-full"></div>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-800 mb-3">
              {this.props.title || "Something went wrong"}
            </h1>
            <p className="text-gray-600 mb-6 text-lg">
              {this.props.message ||
                "We encountered an unexpected error. Don't worry, we're working on fixing it!"}
            </p>

            {/* Error Details (only in development) */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mb-6 text-left bg-gray-50 p-4 rounded-lg border border-gray-200">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 mb-2">
                  Error Details (Development Only)
                </summary>
                <div className="text-xs text-gray-600 font-mono overflow-auto max-h-40">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className="mt-2 whitespace-pre-wrap text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-[#f26522] text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                <FaRedo className="mr-2" />
                Try Again
              </button>

              <a
                href="/"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200"
              >
                <FaHome className="mr-2" />
                Go Home
              </a>
            </div>

            {/* Additional Help */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-2">
                If this problem persists, please contact support.
              </p>
              <a
                href="/feedback"
                className="text-sm text-[#f26522] hover:underline font-medium"
              >
                Report an Issue
              </a>
            </div>
          </div>
        </div>
      );
    }

    // Render children normally if no error
    return this.props.children;
  }
}

export default ErrorBoundary;

